import { useState, useRef } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../lib/firebase";
import { useAuth } from "../../contexts/AuthContext";
import { Upload, Image, Film, Music, CheckCircle, AlertCircle, X } from "lucide-react";

const CATEGORIES = ["Music", "DJ Sets", "Film", "Business", "Gaming", "Previously Recorded"];
const TYPES = [
  { value: "video", label: "Video", icon: Film },
  { value: "audio", label: "Audio / Track", icon: Music },
  { value: "stream", label: "Recorded Stream", icon: Film },
];

export default function DashboardUpload() {
  const { user, profile } = useAuth();
  const [form, setForm] = useState({ title: "", description: "", category: "Music", type: "video" });
  const [mediaFile, setMediaFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState("");
  const [progress, setProgress] = useState({ media: 0, thumbnail: 0 });
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const mediaRef = useRef();
  const thumbRef = useRef();

  const uploadFile = (file, path, key) =>
    new Promise((resolve, reject) => {
      const storageRef = ref(storage, path);
      const task = uploadBytesResumable(storageRef, file);
      task.on("state_changed",
        (snap) => setProgress((p) => ({ ...p, [key]: Math.round((snap.bytesTransferred / snap.totalBytes) * 100) })),
        reject,
        async () => resolve(await getDownloadURL(task.snapshot.ref))
      );
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!mediaFile && !thumbnailFile) { setError("Please select a media file to upload."); return; }
    setUploading(true); setError("");
    try {
      const ts = Date.now();
      let storageURL = "";
      let thumbnailURL = "";

      if (mediaFile) storageURL = await uploadFile(mediaFile, `users/${user.uid}/content/${ts}_${mediaFile.name}`, "media");
      if (thumbnailFile) thumbnailURL = await uploadFile(thumbnailFile, `users/${user.uid}/thumbnails/${ts}_${thumbnailFile.name}`, "thumbnail");

      const slug = (profile?.displayName || "creator").toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9]/g, "");
      await addDoc(collection(db, "content"), {
        creatorId: user.uid,
        creatorSlug: slug,
        creatorName: profile?.displayName || "Creator",
        creatorPhotoURL: profile?.photoURL || "",
        title: form.title,
        description: form.description,
        category: form.category,
        type: form.type,
        storageURL,
        videoURL: form.type === "video" || form.type === "stream" ? storageURL : "",
        thumbnailURL,
        status: "pending",
        views: 0,
        commentCount: 0,
        isLive: false,
        featured: false,
        createdAt: serverTimestamp(),
      });

      setDone(true);
      setForm({ title: "", description: "", category: "Music", type: "video" });
      setMediaFile(null); setThumbnailFile(null); setThumbnailPreview("");
      setProgress({ media: 0, thumbnail: 0 });
    } catch (err) {
      setError(err.message);
    }
    setUploading(false);
  };

  return (
    <div data-testid="dashboard-upload" className="space-y-6 max-w-2xl">
      <h2 className="text-xl font-bold text-white">Upload Content</h2>
      <p className="text-zinc-500 text-sm">Uploaded content will be reviewed by admins before going live.</p>

      {done && (
        <div className="flex items-center gap-3 bg-[#00ff77]/10 border border-[#00ff77]/20 rounded-xl p-4">
          <CheckCircle size={18} className="text-[#00ff77]" />
          <div>
            <p className="text-[#00ff77] font-semibold text-sm">Upload submitted!</p>
            <p className="text-zinc-400 text-xs">Your content is pending admin review.</p>
          </div>
          <button onClick={() => setDone(false)} className="ml-auto text-zinc-500 hover:text-white"><X size={16} /></button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Type selector */}
        <div>
          <label className="text-zinc-400 text-xs font-semibold uppercase tracking-widest mb-2 block">Content Type</label>
          <div className="grid grid-cols-3 gap-2">
            {TYPES.map(({ value, label, icon: Icon }) => (
              <button key={value} type="button" onClick={() => setForm({ ...form, type: value })}
                className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border transition-colors text-xs font-medium ${
                  form.type === value ? "border-[#00ff77]/50 bg-[#00ff77]/10 text-[#00ff77]" : "border-white/10 bg-[#111] text-zinc-400 hover:border-white/20"
                }`}>
                <Icon size={16} />
                {label}
              </button>
            ))}
          </div>
        </div>

        <input data-testid="content-title-input" type="text" placeholder="Title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#00ff77]/50" />

        <textarea data-testid="content-desc-input" placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3}
          className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#00ff77]/50 resize-none" />

        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
          className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#00ff77]/50">
          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>

        {/* Media upload */}
        <div>
          <label className="text-zinc-400 text-xs font-semibold uppercase tracking-widest mb-2 block">Media File</label>
          <div onClick={() => mediaRef.current?.click()}
            className="border-2 border-dashed border-white/10 rounded-xl p-6 text-center cursor-pointer hover:border-[#00ff77]/30 transition-colors">
            {mediaFile ? (
              <div className="flex items-center gap-3 justify-center">
                <Film size={16} className="text-[#00ff77]" />
                <span className="text-white text-sm truncate max-w-xs">{mediaFile.name}</span>
                <button type="button" onClick={(e) => { e.stopPropagation(); setMediaFile(null); }} className="text-zinc-500 hover:text-red-400">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload size={24} className="text-zinc-600" />
                <p className="text-zinc-500 text-sm">Click to select video or audio file</p>
                <p className="text-zinc-700 text-xs">MP4, MOV, MP3, WAV supported</p>
              </div>
            )}
            <input ref={mediaRef} type="file" accept="video/*,audio/*" className="hidden"
              onChange={(e) => e.target.files[0] && setMediaFile(e.target.files[0])} />
          </div>
          {uploading && progress.media > 0 && progress.media < 100 && (
            <div className="mt-2">
              <div className="flex justify-between text-xs text-zinc-500 mb-1"><span>Uploading media...</span><span>{progress.media}%</span></div>
              <div className="h-1.5 bg-[#1a1a1a] rounded-full overflow-hidden">
                <div className="h-full bg-[#00ff77] rounded-full transition-all" style={{ width: `${progress.media}%` }} />
              </div>
            </div>
          )}
        </div>

        {/* Thumbnail */}
        <div>
          <label className="text-zinc-400 text-xs font-semibold uppercase tracking-widest mb-2 block">Thumbnail / Artwork</label>
          <div onClick={() => thumbRef.current?.click()}
            className="border-2 border-dashed border-white/10 rounded-xl p-4 text-center cursor-pointer hover:border-[#00ff77]/30 transition-colors flex items-center gap-4">
            {thumbnailPreview ? (
              <>
                <img src={thumbnailPreview} alt="" className="w-16 h-12 object-cover rounded-lg" />
                <span className="text-white text-sm truncate flex-1">{thumbnailFile?.name}</span>
                <button type="button" onClick={(e) => { e.stopPropagation(); setThumbnailFile(null); setThumbnailPreview(""); }} className="text-zinc-500 hover:text-red-400">
                  <X size={14} />
                </button>
              </>
            ) : (
              <div className="flex items-center gap-3 w-full justify-center">
                <Image size={18} className="text-zinc-600" />
                <span className="text-zinc-500 text-sm">Click to upload thumbnail</span>
              </div>
            )}
            <input ref={thumbRef} type="file" accept="image/*" className="hidden"
              onChange={(e) => { if (e.target.files[0]) { setThumbnailFile(e.target.files[0]); setThumbnailPreview(URL.createObjectURL(e.target.files[0])); }}} />
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl p-3">
            <AlertCircle size={14} className="text-red-400" />
            <p className="text-red-400 text-xs">{error}</p>
          </div>
        )}

        <button data-testid="upload-submit-btn" type="submit" disabled={uploading}
          className="flex items-center gap-2 bg-[#00ff77] text-black font-bold px-6 py-2.5 rounded-full hover:bg-[#00e66b] transition-colors disabled:opacity-50">
          <Upload size={16} />
          {uploading ? `Uploading... ${Math.max(progress.media, progress.thumbnail)}%` : "Submit for Review"}
        </button>
      </form>
    </div>
  );
}
